import { Fragment, useEffect, useState } from "react";
import dayjs, { Dayjs } from "dayjs";

import { Alert, Card, Icon, Typography } from "@mui/material";
import MDBox from "components/MDBox";
import MixedChart from "examples/Charts/MixedChart";

import FormPeriodo from "./FormPeriodo";

import { formatDateEndOfDay } from "helpers/formatDate";

import { IEventoResponse } from "interfaces/eventos";
import { IReports } from "interfaces/reports";
import { formatCurrency } from "helpers/utils";

interface Props {
  evento: IEventoResponse;
  report: IReports[];
}

type dataTotalInscPagaPendType = {
  pagas: Record<string, number>;
  geral: Record<string, number>;
};

type periodoType = {
  inicio: Date | string;
  fim: Date | string;
};

export default function GraficoInscricoes({ evento, report }: Props) {
  const today = new Date();
  const [periodoFilter, setPeriodoFilter] = useState<periodoType | null>(null);
  const [totalInscPagasPend, setTotalInscPagasPend] =
    useState<dataTotalInscPagaPendType>({
      pagas: {},
      geral: {},
    });
  const [valorInscPagas, setValorInscPagas] = useState([]);
  const [valorInscGeral, setValorInscGeral] = useState([]);
  const [lablels, setLablels] = useState<string[]>([]);

  useEffect(() => {
    let valuesPeriodo;
    const fimInscricaoUTC = dayjs(evento?.fimInscricao)
      .add(3, "hour")
      .format("YYYY-MM-DDT23:59:59.000Z");

    (async () => {
      // Definir periodo inicial e final do gráfico
      if (dayjs(fimInscricaoUTC).isBefore(today)) {
        valuesPeriodo = {
          inicio: dayjs(fimInscricaoUTC)
            .subtract(20, "day")
            .format("YYYY-MM-DD"),
          fim: dayjs(fimInscricaoUTC).format("YYYY-MM-DD"),
        };
      } else {
        valuesPeriodo = {
          inicio: dayjs(today).subtract(20, "day").format("YYYY-MM-DD"),
          fim: dayjs(today).format("YYYY-MM-DD"),
        };
      }

      // Definir o periodo no estado
      setPeriodoFilter(valuesPeriodo);

      // Somando o total de inscrições pagas e pendentes por data
      // e definindo os valores para o gráfico
      const sumTotalInscPagasPendentes = await sumTotalInscricaoByDate(report);

      // Gerar os labels para o eixo X do gráfico
      const dataLablels = await generateLabelsEixoX(
        dayjs(valuesPeriodo.inicio),
        dayjs(valuesPeriodo.fim)
      );

      // Gerar o grágico
      await createDataGrafic(dataLablels, sumTotalInscPagasPendentes);

      // Definir os labels no estado
      setLablels(dataLablels);
      // Definir o total de inscrições pagas e pendentes no estado
      setTotalInscPagasPend(sumTotalInscPagasPendentes);
    })();
  }, [evento, report]);

  // Gerar os dados para o gráfico
  async function createDataGrafic(
    lablelsEixoX: string[],
    valuesGraphic: dataTotalInscPagaPendType
  ) {
    let pagas: number[] = [];
    let geral: number[] = [];

    lablelsEixoX &&
      lablelsEixoX.forEach((label) => {
        const valorGeral = valuesGraphic?.geral[label]
          ? valuesGraphic?.geral[label]
          : 0;
        const valorPagas = valuesGraphic?.pagas[label]
          ? valuesGraphic?.pagas[label]
          : 0;
        pagas.push(valorPagas);
        geral.push(valorGeral);
      });
    setValorInscPagas(pagas);
    setValorInscGeral(geral);
  }

  // Somar o totalInscricao geral - Realizadas e PAGAS
  async function sumTotalInscricaoByDate(
    report: IReports[]
  ): Promise<dataTotalInscPagaPendType> {
    try {
      return report.reduce(
        (acc: dataTotalInscPagaPendType, curr) => {
          // Extrair apenas a data de dataInscricao (removendo o horário)
          const date = dayjs(
            new Date(curr.dataInscricao).toISOString().split("T")[0]
          ).format("DD/MM");

          // status da inscrição
          const statusInsc = curr.statusInscricao.tipo;

          // Converter totalInscricao para número
          const totalInscricao = parseFloat(curr.totalInscricao);

          // Inicializar os valores para a data, se necessário
          if (!acc.pagas[date]) acc.pagas[date] = 0;
          if (!acc.geral[date]) acc.geral[date] = 0;

          // Somar o totalInscricao geral - Realizadas
          acc.geral[date] += totalInscricao;

          // Somar o totalInscricao agrupado pela data e status PAGO
          if (statusInsc === "Pago") {
            acc.pagas[date] += totalInscricao;
          }

          return acc;
        },
        { pagas: {}, geral: {} }
      );
    } catch (error) {
      return { pagas: {}, geral: {} };
    }
  }
  // Função para gerar os labels do eixo X com base em duas datas
  async function generateLabelsEixoX(
    startDate: Dayjs,
    endDate: Dayjs
  ): Promise<string[]> {
    let currentDate = dayjs(startDate);
    const dates = [];
    const finalDate = dayjs(endDate);

    while (currentDate.isBefore(finalDate) || currentDate.isSame(finalDate)) {
      dates.push(currentDate.format("DD/MM"));
      currentDate = currentDate.add(1, "day");
    }

    return dates;
  }

  // Função para gerar os labels do eixo X com base em duas datas
  async function handleFilterChange(
    startDate: Date | string,
    endDate: Date | string
  ) {
    setPeriodoFilter({
      inicio: dayjs(startDate).format("YYYY-MM-DD"),
      fim: dayjs(endDate).format("YYYY-MM-DD"),
    });
    // Gerar os labels para o eixo X do gráfico
    const newLables = await generateLabelsEixoX(
      dayjs(startDate),
      dayjs(endDate)
    );
    setLablels(newLables);
    createDataGrafic(newLables, totalInscPagasPend);
  }

  function calculatePercentageChange(): JSX.Element {
    const todayKey = dayjs(today).format("DD/MM");
    const yesterdayKey = dayjs(today).subtract(1, "day").format("DD/MM");

    const valorRecHoje = totalInscPagasPend?.pagas[todayKey] || 0;
    const valorRecOntem = totalInscPagasPend?.pagas[yesterdayKey] || 0;

    const difference = valorRecHoje - valorRecOntem;
    const aumentoReceita = difference > 0;

    const percentageChange =
      valorRecOntem === 0 ? 100 : (difference / valorRecOntem) * 100;

    const renderPercentageIcon = () => (
      <Typography variant="caption">
        {Math.abs(percentageChange).toFixed(1)}%{" "}
        <Icon color={aumentoReceita ? "success" : "error"}>
          {aumentoReceita ? "arrow_upward" : "arrow_downward"}
        </Icon>
      </Typography>
    );

    const renderRevenueInfo = () => (
      <MDBox display="flex" flexDirection="column">
        <MDBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="h5">
            Hoje: {formatCurrency(valorRecHoje)}
          </Typography>
          {difference !== 0 ? (
            renderPercentageIcon()
          ) : (
            <Icon color="warning">multiple_stop</Icon>
          )}
        </MDBox>
        <Typography variant="caption">
          Ontem: {formatCurrency(valorRecOntem)}
        </Typography>
      </MDBox>
    );

    const severity =
      difference === 0 ? "info" : aumentoReceita ? "success" : "error";

    return (
      <Alert severity={severity} sx={{ m: 1 }}>
        <MDBox display="flex" flexDirection="column">
          {renderRevenueInfo()}
        </MDBox>
      </Alert>
    );
  }

  // Caso não exista o evento ou o report, exibe mensagem de carregamento
  if (!evento || !report) {
    return (
      <MDBox
        display="flex"
        justifyContent="center"
        alignItems="center"
        height={120}
      >
        <Typography variant="subtitle1">
          ⏳ Carregando dados do gráfico...
        </Typography>
      </MDBox>
    );
  }

  return (
    <Card
      sx={{
        display: "flex",
        mt: 2,
        overflow: "hidden",
      }}
    >
      <Fragment>
        {calculatePercentageChange()}
        <FormPeriodo
          title={evento?.titulo}
          dateInitial={periodoFilter?.inicio}
          dateEnd={periodoFilter?.fim}
          onChange={handleFilterChange}
        />
        {/* Grafico */}
        <MixedChart
          height="400px"
          title="Inscrições por Dia: (valores em R$)"
          description={`Perido de inscrições: ${formatDateEndOfDay(
            evento?.inicioIncricao
          )} à  ${formatDateEndOfDay(evento?.fimInscricao)}`}
          chart={{
            labels: lablels,
            datasets: [
              {
                chartType: "gradient-line",
                label: "Pagas",
                color: "info",
                data: valorInscPagas,
              },
              {
                chartType: "thin-bar",
                label: "Realizadas",
                color: "dark",
                data: valorInscGeral,
              },
            ],
          }}
        />
      </Fragment>
    </Card>
  );
}
